import React, { memo, useEffect, useRef, useState } from "react";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Box, IconButton, Sheet, Stack, Typography } from "@mui/joy";
import CustomSwitch from "components/common/CustomSwitch";
import { theme } from "styles/theme";
import { TaskEditorState } from "./utils/taskEditorUtils";
import PreviewBlock from "./preview/PreviewBlock";
import PreviewGoNextButton from "./preview/PreviewGoNextButton";
import usePreviewSession from "./preview/usePreviewSession";

interface TaskEditorLivePreviewProps {
  task?: TaskEditorState;
}

const TASK_EDITOR_PREVIEW_SYNC_DELAY_MS = 300;

const previewTabs = [
  { id: "patient", label: "내담자" },
  { id: "coach", label: "코치" },
] as const;

type ViewerMode = "patient" | "coach";

// 빈 상태 (task 없을 때)
function EmptyState() {
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: "12px",
        border: "1px dashed",
        borderColor: theme.vars.palette.Border.Weak,
        backgroundColor: theme.vars.palette.Background.Level1,
        textAlign: "center",
      }}
    >
      <Typography
        variant="Footnote"
        sx={{
          color: theme.vars.palette.Content.Weak,
          fontSize: "12px",
        }}
      >
        활동지를 선택하면 미리보기가 표시됩니다.
      </Typography>
    </Box>
  );
}

// 코치 모드 — 아직 미구현, "준비중" placeholder
function CoachModePlaceholder() {
  return (
    <Box
      sx={{
        py: "48px",
        px: "24px",
        borderRadius: "12px",
        border: "1px dashed",
        borderColor: theme.vars.palette.Border.Weak,
        backgroundColor: "transparent",
        textAlign: "center",
      }}
    >
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 600,
          color: theme.vars.palette.Content.Weak,
        }}
      >
        준비 중 - 꼭 필요할까..?
      </Typography>
    </Box>
  );
}

// 뷰어 모드 세그먼트 컨트롤 — 컴팩트한 토글
function ViewerSegmentedControl({
  viewerMode,
  onChange,
}: {
  viewerMode: ViewerMode;
  onChange: (mode: ViewerMode) => void;
}) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        height: "28px",
        p: "2px",
        gap: "2px",
        borderRadius: "8px",
        backgroundColor: theme.vars.palette.Surface.Backward,
        flexShrink: 0,
      }}
    >
      {previewTabs.map((tab) => {
        const isActive = tab.id === viewerMode;
        return (
          <Box
            key={tab.id}
            onClick={() => onChange(tab.id)}
            sx={{
              minWidth: "52px",
              px: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: isActive ? 700 : 500,
              color: isActive
                ? theme.vars.palette.Content.Strong
                : theme.vars.palette.Content.Weak,
              backgroundColor: isActive
                ? theme.vars.palette.Surface.Forward
                : "transparent",
              boxShadow: isActive ? "0 1px 2px rgba(0, 0, 0, 0.06)" : "none",
              transition: "background-color 0.15s, color 0.15s",
              userSelect: "none",
              "&:hover": {
                color: theme.vars.palette.Content.Strong,
              },
            }}
          >
            {tab.label}
          </Box>
        );
      })}
    </Box>
  );
}

// 체험 모드 + Refresh 컨트롤 (patient 탭에서만 노출)
function TrialControls({
  isTrialEnabled,
  onToggleTrial,
  onRefresh,
}: {
  isTrialEnabled: boolean;
  onToggleTrial: (enabled: boolean) => void;
  onRefresh: () => void;
}) {
  return (
    <Stack
      direction="row"
      spacing="6px"
      sx={{ alignItems: "center", flexShrink: 0 }}
    >
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 500,
          color: theme.vars.palette.Content.Weak,
        }}
      >
        체험 모드
      </Typography>
      <CustomSwitch
        checked={isTrialEnabled}
        onChange={(event) => onToggleTrial(event.target.checked)}
        slotProps={{ input: { "aria-label": "체험 모드" } }}
      />
      {/* refresh 버튼 자리는 체험 모드 OFF 일 때도 reserve 해 Switch 가 좌우로
          흔들리지 않도록 함 — visibility 만 토글. */}
      <Box
        sx={{
          width: "1px",
          height: "12px",
          backgroundColor: theme.vars.palette.Border.Weak,
          mx: "2px",
          visibility: isTrialEnabled ? "visible" : "hidden",
        }}
      />
      <IconButton
        variant="plain"
        color="neutral"
        size="sm"
        aria-label="프리뷰 새로고침"
        onClick={onRefresh}
        sx={{
          minWidth: "26px",
          minHeight: "26px",
          borderRadius: "6px",
          color: theme.vars.palette.Content.Weak,
          visibility: isTrialEnabled ? "visible" : "hidden",
          "&:hover": {
            color: theme.vars.palette.Content.Highlight,
          },
        }}
      >
        <RefreshRoundedIcon sx={{ fontSize: "16px" }} />
      </IconButton>
    </Stack>
  );
}

// 헤더 — 체험 모드 토글 + 리프레시. 내담자/코치 세그먼트는 제거됨.
function PreviewHeader({
  isTrialEnabled,
  onToggleTrial,
  onRefresh,
}: {
  isTrialEnabled: boolean;
  onToggleTrial: (enabled: boolean) => void;
  onRefresh: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        flexShrink: 0,
      }}
    >
      <TrialControls
        isTrialEnabled={isTrialEnabled}
        onToggleTrial={onToggleTrial}
        onRefresh={onRefresh}
      />
    </Box>
  );
}

function TaskEditorLivePreview({ task }: TaskEditorLivePreviewProps) {
  // 내담자/코치 탭이 제거되어 항상 내담자 시점으로 고정.
  const viewerMode: ViewerMode = "patient";
  const [isTrialEnabled, setIsTrialEnabled] = useState(false);
  const [previewTask, setPreviewTask] = useState(task);

  // 300ms 디바운스 sync
  useEffect(() => {
    if (!task) return;
    const t = window.setTimeout(
      () => setPreviewTask(task),
      TASK_EDITOR_PREVIEW_SYNC_DELAY_MS
    );
    return () => window.clearTimeout(t);
  }, [task]);

  const session = usePreviewSession(previewTask, {
    viewerMode,
    isTrialEnabled,
  });

  const isInteractive = viewerMode === "patient" && isTrialEnabled;
  const blockRefs = useRef<Map<number, HTMLElement>>(new Map());
  const prevRevealedUpToRef = useRef(0);

  // v1 호환: Enter 키로 다음 블록 이동. textarea/input 안에서는 줄바꿈/기본 동작 유지.
  useEffect(() => {
    if (!isInteractive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;
      if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === "textarea" || tag === "input") return;
      const upTo = session.state.revealedUpTo;
      const totalBlocks = previewTask?.blocks.length ?? 0;
      if (upTo <= 0 || upTo >= totalBlocks) return;
      const lastIdx = upTo - 1;
      const block = previewTask?.blocks[lastIdx];
      // 분기 블록은 옵션 클릭으로만 진행
      if (block?.action?.type === "button_branch") return;
      if (!session.isBlockComplete(lastIdx)) return;
      e.preventDefault();
      session.advanceFrom(lastIdx);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isInteractive, session, previewTask]);

  // 새로 reveal 된 블록으로 부드럽게 스크롤. 초기 reveal 이거나 reset 으로
  // upTo 가 줄어든 경우엔 스크롤하지 않는다.
  useEffect(() => {
    const upTo = session.state.revealedUpTo;
    const prev = prevRevealedUpToRef.current;
    prevRevealedUpToRef.current = upTo;
    if (!isInteractive) return;
    if (prev === 0 || upTo <= prev) return;
    const node = blockRefs.current.get(upTo);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [session.state.revealedUpTo, isInteractive]);

  if (!task) {
    return <EmptyState />;
  }

  const mode: "static" | "interactive" = isInteractive
    ? "interactive"
    : "static";

  // 체험 모드를 처음 상태로 되돌린다. revealedUpTo 와 입력값을 모두 비우고,
  // 사용자가 다시 첫 블록부터 진행할 수 있게 한다. 체험 모드 OFF 일 때는
  // 시각상 모든 블록이 그대로 보여 변화가 없는 게 정상.
  const handleRefresh = () => {
    session.reset();
  };

  const blocks = previewTask?.blocks ?? [];

  return (
    <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }}>
      <PreviewHeader
        isTrialEnabled={isTrialEnabled}
        onToggleTrial={setIsTrialEnabled}
        onRefresh={handleRefresh}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          backgroundColor: theme.vars.palette.Background.Level1,
          borderRadius: "12px",
          p: "12px",
        }}
      >
        {blocks.length === 0 ? (
          <Sheet
            sx={{
              border: "1px dashed",
              borderColor: theme.vars.palette.Border.Weak,
              backgroundColor: "transparent",
              borderRadius: "8px",
              p: "16px",
              textAlign: "center",
            }}
          >
            <Typography
              variant="Caption"
              sx={{ color: theme.vars.palette.Content.Weak }}
            >
              (블록 없음)
            </Typography>
          </Sheet>
        ) : (
          <Stack
            spacing={1}
            sx={{ maxWidth: "660px", mx: "auto", width: "100%" }}
          >
            {blocks.map((block, i) => {
              const seq = i + 1;

              // 체험 모드: reveal 범위 바깥 블록 숨김
              if (mode === "interactive" && seq > session.state.revealedUpTo) {
                return null;
              }
              // 분기로 숨겨진 블록 숨김
              if (session.state.hiddenSequences.has(seq)) {
                return null;
              }

              const isLastRevealed =
                mode === "interactive" && seq === session.state.revealedUpTo;
              // header 블록은 단독으로 reveal 되지 않고 다음 body 와 묶여 보이므로
              // 끝에 "다음" 화살표가 붙지 않는다.
              const showGoNext =
                isLastRevealed &&
                seq < blocks.length &&
                block.type !== "header" &&
                block.action?.type !== "button_branch";

              return (
                <Stack
                  key={i}
                  spacing={1}
                  ref={(el) => {
                    if (el) blockRefs.current.set(seq, el);
                    else blockRefs.current.delete(seq);
                  }}
                >
                  <PreviewBlock
                    block={block}
                    blockSequence={seq}
                    mode={mode}
                    session={session}
                    viewerMode={viewerMode}
                    totalBlocks={blocks.length}
                  />
                  {showGoNext && (
                    <PreviewGoNextButton
                      disabled={!session.isBlockComplete(i)}
                      onClick={() => session.advanceFrom(i)}
                    />
                  )}
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

export default memo(TaskEditorLivePreview);
